"use client";

import type { ReactNode } from "react";
import Link from "next/link";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { customerSupportEmail, customerSupportEmailHref } from "@/shared/config/customerSupport";

const accent = "#075fc9";

type PageSection = {
  id: string;
  title: string;
};

const sections: PageSection[] = [
  { id: "overview", title: "Deactivate vs. delete" },
  { id: "web", title: "On the Carevinco website" },
  { id: "mobile", title: "On the Carevinco app" },
  { id: "what-happens", title: "What happens next" },
];

function StepList({ steps }: { steps: readonly ReactNode[] }) {
  return (
    <Box component="ol" sx={{ color: "#4e596d", display: "grid", gap: 1.4, m: 0, pl: 3 }}>
      {steps.map((step, index) => (
        <Typography component="li" key={index} sx={{ fontSize: 16, lineHeight: 1.75, pl: 0.5 }}>
          {step}
        </Typography>
      ))}
    </Box>
  );
}

function PlatformCard({ children, title }: { children: ReactNode; title: string }) {
  return (
    <Box
      sx={{
        bgcolor: "#f8fafc",
        border: "1px solid #e1e7ef",
        borderRadius: "8px",
        p: { xs: 2.5, sm: 3 },
      }}
    >
      <Typography component="h3" sx={{ color: "#071e62", fontSize: 17, fontWeight: 900, mb: 1.75 }}>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

export function AccountDeletionPage() {
  return (
    <Box className="legal-page" component="main" id="legal-page-top" sx={{ bgcolor: "#ffffff", color: "#10182f" }}>
      <Box
        className="legal-page__hero"
        component="section"
        sx={{
          bgcolor: "#071e62",
          borderTop: `4px solid ${accent}`,
          color: "#ffffff",
          overflow: "hidden",
          py: { xs: 6, md: 8 },
        }}
      >
        <Container maxWidth="lg">
          <Stack alignItems="flex-start" direction={{ xs: "column", sm: "row" }} gap={{ xs: 2.5, sm: 3 }} sx={{ maxWidth: 820 }}>
            <Box
              sx={{
                alignItems: "center",
                bgcolor: accent,
                border: "1px solid rgba(255,255,255,0.3)",
                borderRadius: "8px",
                color: "#ffffff",
                display: "flex",
                flex: "0 0 auto",
                height: 64,
                justifyContent: "center",
                width: 64,
              }}
            >
              <DeleteOutlineRoundedIcon sx={{ fontSize: 34 }} />
            </Box>
            <Box>
              <Typography
                component="p"
                sx={{ color: accent, fontSize: 13, fontWeight: 900, letterSpacing: 0, mb: 1, textTransform: "uppercase" }}
              >
                Account &amp; data
              </Typography>
              <Typography component="h1" sx={{ fontSize: { xs: 34, sm: 42, md: 48 }, fontWeight: 900, letterSpacing: 0, lineHeight: 1.08 }}>
                Delete or Deactivate Your Account
              </Typography>
              <Typography sx={{ color: "rgba(255,255,255,0.78)", fontSize: { xs: 16, md: 18 }, lineHeight: 1.7, mt: 2, maxWidth: 760 }}>
                Every Carevinco account — customer or provider — can be deactivated or permanently deleted at any
                time, directly from the app or website. You do not need to contact us first.
              </Typography>
            </Box>
          </Stack>
        </Container>
      </Box>

      <Box className="legal-page__content" component="section" sx={{ py: { xs: 5, md: 8 } }}>
        <Container maxWidth="lg">
          <Box
            sx={{
              alignItems: "start",
              display: "grid",
              gap: { xs: 4, md: 7 },
              gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "230px minmax(0, 1fr)" },
            }}
          >
            <Box
              component="aside"
              sx={{
                bgcolor: "#f3f7fc",
                border: "1px solid #dce6f3",
                borderRadius: "8px",
                p: 2.25,
                position: { md: "sticky" },
                top: { md: 104 },
              }}
            >
              <Typography component="h2" sx={{ color: "#071e62", fontSize: 15, fontWeight: 900, mb: 1.5 }}>
                On this page
              </Typography>
              <Stack component="nav" aria-label="Delete or deactivate your account sections" spacing={0.25}>
                {sections.map((section) => (
                  <Box
                    component="a"
                    href={`#${section.id}`}
                    key={section.id}
                    sx={{
                      borderLeft: "3px solid transparent",
                      color: "#526078",
                      display: "block",
                      fontSize: 13.5,
                      fontWeight: 700,
                      lineHeight: 1.4,
                      px: 1.25,
                      py: 0.9,
                      textDecoration: "none",
                      "&:hover, &:focus-visible": { borderLeftColor: accent, color: "#075fc9" },
                    }}
                  >
                    {section.title}
                  </Box>
                ))}
              </Stack>
            </Box>

            <Box className="legal-page__article" component="article" sx={{ minWidth: 0 }}>
              <Box component="section" id="overview" sx={{ scrollMarginTop: 112, pb: 4.5 }}>
                <Stack alignItems="flex-start" direction="row" gap={2}>
                  <Box aria-hidden="true" sx={{ bgcolor: accent, borderRadius: "2px", flex: "0 0 auto", height: 28, mt: 0.25, width: 4 }} />
                  <Typography component="h2" sx={{ color: "#071e62", fontSize: { xs: 23, md: 27 }, fontWeight: 900, letterSpacing: 0, lineHeight: 1.25 }}>
                    Deactivate vs. delete
                  </Typography>
                </Stack>
                <Stack spacing={2} sx={{ mt: 2.25 }}>
                  <Typography sx={{ color: "#4e596d", fontSize: 16, lineHeight: 1.85 }}>
                    <strong>Deactivate</strong> hides your account and signs you out immediately. Nothing is deleted
                    — sign back in anytime to reactivate automatically.
                  </Typography>
                  <Typography sx={{ color: "#4e596d", fontSize: 16, lineHeight: 1.85 }}>
                    <strong>Delete</strong> permanently removes your account. It is scheduled instantly, you&apos;re
                    signed out immediately, and it completes after a 14-day window. During those 14 days you can
                    contact support to cancel the request.
                  </Typography>
                </Stack>
                <Divider sx={{ borderColor: "#e3e9f1", mt: 4.5 }} />
              </Box>

              <Box component="section" id="web" sx={{ scrollMarginTop: 112, pb: 4.5 }}>
                <Stack alignItems="flex-start" direction="row" gap={2}>
                  <Box aria-hidden="true" sx={{ bgcolor: accent, borderRadius: "2px", flex: "0 0 auto", height: 28, mt: 0.25, width: 4 }} />
                  <Typography component="h2" sx={{ color: "#071e62", fontSize: { xs: 23, md: 27 }, fontWeight: 900, letterSpacing: 0, lineHeight: 1.25 }}>
                    On the Carevinco website
                  </Typography>
                </Stack>
                <Stack spacing={2.5} sx={{ mt: 2.25 }}>
                  <PlatformCard title="Customers">
                    <StepList
                      steps={[
                        <>Sign in at carevinco.com and open <strong>Settings</strong>.</>,
                        <>Scroll to the <strong>Account</strong> section at the bottom.</>,
                        <>Choose <strong>Deactivate</strong> or <strong>Delete Account</strong>, then confirm.</>,
                      ]}
                    />
                  </PlatformCard>
                  <PlatformCard title="Providers (nurses &amp; caregivers)">
                    <StepList
                      steps={[
                        <>Sign in at carevinco.com and open your <strong>Provider Profile</strong>.</>,
                        <>Scroll down to the <strong>Account</strong> card.</>,
                        <>Choose <strong>Deactivate</strong> or <strong>Delete Account</strong>, then confirm.</>,
                      ]}
                    />
                  </PlatformCard>
                </Stack>
                <Divider sx={{ borderColor: "#e3e9f1", mt: 4.5 }} />
              </Box>

              <Box component="section" id="mobile" sx={{ scrollMarginTop: 112, pb: 4.5 }}>
                <Stack alignItems="flex-start" direction="row" gap={2}>
                  <Box aria-hidden="true" sx={{ bgcolor: accent, borderRadius: "2px", flex: "0 0 auto", height: 28, mt: 0.25, width: 4 }} />
                  <Typography component="h2" sx={{ color: "#071e62", fontSize: { xs: 23, md: 27 }, fontWeight: 900, letterSpacing: 0, lineHeight: 1.25 }}>
                    On the Carevinco app
                  </Typography>
                </Stack>
                <Stack spacing={2.5} sx={{ mt: 2.25 }}>
                  <PlatformCard title="Customers">
                    <StepList
                      steps={[
                        <>Open the Carevinco app and tap the <strong>Profile</strong> tab.</>,
                        <>Tap <strong>Privacy &amp; Security</strong>.</>,
                        <>Scroll to the <strong>Account</strong> section, then choose <strong>Deactivate</strong> or <strong>Delete Account</strong>.</>,
                      ]}
                    />
                  </PlatformCard>
                  <PlatformCard title="Providers (nurses &amp; caregivers)">
                    <StepList
                      steps={[
                        <>Open the Carevinco Provider app and tap the <strong>Profile</strong> tab.</>,
                        <>Tap <strong>Privacy &amp; Security</strong>.</>,
                        <>Scroll to the <strong>Account</strong> section, then choose <strong>Deactivate</strong> or <strong>Delete Account</strong>.</>,
                      ]}
                    />
                  </PlatformCard>
                </Stack>
                <Divider sx={{ borderColor: "#e3e9f1", mt: 4.5 }} />
              </Box>

              <Box component="section" id="what-happens" sx={{ scrollMarginTop: 112, pb: 4.5 }}>
                <Stack alignItems="flex-start" direction="row" gap={2}>
                  <Box aria-hidden="true" sx={{ bgcolor: accent, borderRadius: "2px", flex: "0 0 auto", height: 28, mt: 0.25, width: 4 }} />
                  <Typography component="h2" sx={{ color: "#071e62", fontSize: { xs: 23, md: 27 }, fontWeight: 900, letterSpacing: 0, lineHeight: 1.25 }}>
                    What happens next
                  </Typography>
                </Stack>
                <Box component="ul" sx={{ color: "#4e596d", display: "grid", gap: 1.2, m: 0, mt: 2.25, pl: 3 }}>
                  <Typography component="li" sx={{ fontSize: 16, lineHeight: 1.7, pl: 0.5 }}>
                    Your account is hidden from matching and bookings immediately.
                  </Typography>
                  <Typography component="li" sx={{ fontSize: 16, lineHeight: 1.7, pl: 0.5 }}>
                    Sign-in access is fully removed within 14 days of your request.
                  </Typography>
                  <Typography component="li" sx={{ fontSize: 16, lineHeight: 1.7, pl: 0.5 }}>
                    You can contact support to cancel a pending deletion at any point during those 14 days.
                  </Typography>
                  <Typography component="li" sx={{ fontSize: 16, lineHeight: 1.7, pl: 0.5 }}>
                    Some information, such as your name, phone number, and email, may be retained afterward where
                    needed for legal, safety, financial record-keeping, or fraud-prevention purposes.
                  </Typography>
                </Box>
                <Typography sx={{ color: "#4e596d", fontSize: 16, lineHeight: 1.85, mt: 2.25 }}>
                  See our{" "}
                  <Link href="/privacy-policy" style={{ color: accent, fontWeight: 700, textDecoration: "underline" }}>
                    Privacy Policy
                  </Link>{" "}
                  for full details on what we collect, retain, and why.
                </Typography>
              </Box>

              <Box
                sx={{
                  alignItems: { xs: "flex-start", sm: "center" },
                  bgcolor: "#f3f7fc",
                  border: "1px solid #dce6f3",
                  borderLeft: `4px solid ${accent}`,
                  borderRadius: "8px",
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  gap: 2,
                  justifyContent: "space-between",
                  mt: 2,
                  p: { xs: 2.5, sm: 3 },
                }}
              >
                <Box>
                  <Typography sx={{ color: "#071e62", fontSize: 18, fontWeight: 900 }}>
                    Need help with account deletion?
                  </Typography>
                  <Typography sx={{ color: "#59657a", lineHeight: 1.6, mt: 0.5 }}>
                    Our support team can help at {customerSupportEmail}.
                  </Typography>
                </Box>
                <Button
                  component="a"
                  href={customerSupportEmailHref}
                  startIcon={<MailOutlineRoundedIcon />}
                  variant="contained"
                  sx={{ bgcolor: "#075fc9", borderRadius: "8px", boxShadow: "none", flex: "0 0 auto", fontWeight: 850, minHeight: 44, px: 2.25, "&:hover": { bgcolor: "#064fa8", boxShadow: "none" } }}
                >
                  Email support
                </Button>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}
